import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Modal } from '@/components/ui/modal'
import { Badge } from '@/components/ui/badge'

export interface MetricEditData {
  id: number
  name: string
  description: string
  unit: string
  visibleToClient: boolean
  calculationMode: 'incremental' | 'overwrite'
}

export interface EditMetricModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: MetricEditData) => void | Promise<void>
  isLoading?: boolean
  metric: MetricEditData | null
}

export function EditMetricModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  metric,
}: EditMetricModalProps) {
  const [formData, setFormData] = useState<MetricEditData>({
    id: 0,
    name: '',
    description: '',
    unit: '',
    visibleToClient: true,
    calculationMode: 'incremental',
  })

  // Update form data when metric prop changes
  useEffect(() => {
    if (metric) {
      setFormData({
        id: metric.id,
        name: metric.name,
        description: metric.description,
        unit: metric.unit,
        visibleToClient: metric.visibleToClient,
        calculationMode: metric.calculationMode,
      })
    }
  }, [metric])

  const handleInputChange = (field: keyof MetricEditData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.unit.trim()) {
      return
    }

    await onSubmit(formData)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Enregistrer les modifications
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Modifier la métrique</h2>
            <p className="text-sm text-gray-500 mt-1">
              Modifiez les informations de la métrique (le mode de calcul ne peut pas être changé)
            </p>
          </div>
        </div>

        <FormField label="Nom de la métrique" required>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Ex: Messages traités"
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        <FormField label="Description" required>
          <Textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Description de la métrique visible par le client"
            rows={3}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
          />
        </FormField>

        <FormField label="Unité" required>
          <Input
            type="text"
            value={formData.unit}
            onChange={(e) => handleInputChange('unit', e.target.value)}
            placeholder="Ex: messages, €, %, secondes"
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Mode de calcul</label>
          <div className="flex items-center gap-3">
            <Badge 
              variant="secondary" 
              className="bg-gray-100 text-gray-600 border-gray-300 px-4 py-2"
            >
              {formData.calculationMode === 'incremental' ? 'Incrémental' : 'Écrasement'}
            </Badge>
            <span className="text-sm text-gray-500">
              (Non modifiable après création)
            </span>
          </div>
          {formData.calculationMode === 'incremental' && (
            <p className="text-sm text-gray-500 mt-2">
              Les nouvelles valeurs s'ajoutent aux précédentes
            </p>
          )}
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">
              Visible par le client
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Le client pourra voir cette métrique dans son dashboard
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleInputChange('visibleToClient', !formData.visibleToClient)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.visibleToClient ? 'bg-cyan-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.visibleToClient ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </Modal>
  )
}

